import type React from 'react'
import { forwardRef } from 'react'
import './BaseInput.scss'

export interface BaseInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'height'> {
  backgroundColor?: string
  label?: React.ReactNode
  labelStyles?: React.CSSProperties
  error?: boolean
  color?: string
  border?: string
  padding?: string
  margin?: string
  height?: string
  borderRadius?: string
  borderColorOnFocus?: string
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  labelOnBottom?: boolean
  dataTest?: string
  required?: boolean
}

const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      labelOnBottom,
      style,
      error,
      labelStyles,
      backgroundColor,
      borderColorOnFocus,
      border,
      padding,
      borderRadius,
      color,
      margin,
      height,
      label = false,
      placeholder,
      type,
      onChange,
      value,
      disabled,
      startAdornment,
      endAdornment,
      className,
      dataTest,
      autoFocus = false,
      required = false,
      ...rest
    },
    ref,
  ) => {
    return (
      <div className={`base-input-container ${className || ''}`} style={style} data-test={dataTest}>
        {!labelOnBottom && label !== false && (
          <div className="base-input-label" style={labelStyles}>
            {label}
            {required && <span className="base-input-required">*</span>}
          </div>
        )}

        <div
          className={`base-input-wrapper ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
          style={{
            backgroundColor: backgroundColor || 'var(--background-surface)',
            margin: margin,
            height,
            borderRadius: borderRadius || '8px',
            border: border,
            padding: padding,
            ...(error && { borderColor: 'var(--border-error)' }),
          }}
          data-border-focus={borderColorOnFocus}
          data-workflow-field
        >
          {startAdornment && <div className="base-input-adornment start">{startAdornment}</div>}

          <input
            ref={ref}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            autoFocus={autoFocus}
            className="base-input-field"
            data-workflow-bare
            style={{ color: color }}
            onFocus={(e) => {
              // .select() is only meaningful for text-like inputs; calling it on
              // date/time/color/file inputs is a no-op or throws in some browsers.
              const isTextLike = !['date', 'time', 'datetime-local', 'month', 'week', 'color', 'file', 'range'].includes(
                e.currentTarget.type,
              )
              if (isTextLike) e.currentTarget.select()
              rest.onFocus?.(e)
            }}
            onClick={(e) => {
              // For date/time pickers, preventDefault blocks the native picker UI.
              const opensPicker = ['date', 'time', 'datetime-local', 'month', 'week'].includes(e.currentTarget.type)
              if (!opensPicker) e.preventDefault()
              e.stopPropagation()
            }}
            {...rest}
          />

          {endAdornment && <div className="base-input-adornment end">{endAdornment}</div>}
        </div>

        {labelOnBottom && label !== false && (
          <div className="base-input-label label-bottom" style={{ marginTop: 5, ...labelStyles }}>
            {label}
            {required && <span className="base-input-required">*</span>}
          </div>
        )}
      </div>
    )
  },
)

BaseInput.displayName = 'BaseInput'

export default BaseInput
